import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import DocumentExplanation from "@/components/BasProcess/DocumentExplanation";
import DetailLevelPanel from "@/components/BasProcess/DetailLevelPanel";
import {
  PROCESS_STEPS,
  PROCESS_PHASES,
  STEP_STATUS_LABELS,
  type StepStatus,
  type ProcessPhase,
} from "@/lib/basProcess/constants";
import { AMP_TEMPLATE } from "@/lib/amp/constants";
import { CheckCircle2, Circle, Clock, MinusCircle, ChevronRight } from "lucide-react";

interface Props {
  projectId: string;
  onOpenLink?: (link: string) => void;
}

interface StepRow {
  doc_number: string;
  status: StepStatus;
  note: string | null;
}

const statusIcon = (s: StepStatus) => {
  if (s === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (s === "in_progress") return <Clock className="h-4 w-4 text-amber-600" />;
  if (s === "not_applicable") return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
  return <Circle className="h-4 w-4 text-muted-foreground" />;
};

const statusVariant = (s: StepStatus) =>
  s === "done" ? "default" : s === "in_progress" ? "secondary" : "outline";

interface ExternalRow {
  doc_number: string;
  completed: boolean;
  completed_at: string | null;
  responsible: string | null;
  url: string | null;
  comment: string | null;
}

export const ProcessRoadmap: React.FC<Props> = ({ projectId, onOpenLink }) => {
  const [rows, setRows] = useState<Record<string, StepRow>>({});
  const [external, setExternal] = useState<Record<string, ExternalRow>>({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [ampSummary, setAmpSummary] = useState<{ done: number; total: number; missing: string[] } | null>(null);

  useEffect(() => {
    const loadExternal = async () => {
      const { data } = await supabase
        .from("external_step_completions")
        .select("doc_number, completed, completed_at, responsible, url, comment")
        .eq("project_id", projectId);
      const map: Record<string, ExternalRow> = {};
      (data || []).forEach((r: any) => (map[r.doc_number] = r));
      setExternal(map);
    };
    loadExternal();
  }, [projectId]);


  useEffect(() => {
    const loadAmp = async () => {
      const { data } = await supabase
        .from("amp_sections")
        .select("section_key, status, source_meta")
        .eq("project_id", projectId);
      if (!data?.length) {
        setAmpSummary(null);
        return;
      }
      const relevant = data.filter((r: any) => r.status !== "not_applicable");
      const done = relevant.filter((r: any) => r.status === "done").length;
      const missing = Array.from(
        new Set(
          data
            .map((r: any) => {
              const def = AMP_TEMPLATE.find((d) => d.key === r.section_key);
              if (!def?.source || !def.docRefs?.length) return null;
              const meta = r.source_meta || {};
              return (meta.count ?? 0) > 0 ? null : `Dok ${def.docRefs[0].doc} ${def.docRefs[0].title}`;
            })
            .filter(Boolean) as string[]
        )
      );
      setAmpSummary({ done, total: relevant.length, missing });
    };
    loadAmp();
  }, [projectId]);

  const loadSteps = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("process_steps")
      .select("doc_number, status, note")
      .eq("project_id", projectId);
    if (error) {
      toast.error("Kunde inte läsa processteg");
    } else {
      const map: Record<string, StepRow> = {};
      (data || []).forEach((r: any) => (map[r.doc_number] = r));
      setRows(map);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void loadSteps();
  }, [loadSteps]);

  const upsert = async (docNumber: string, patch: Partial<StepRow>) => {
    const def = PROCESS_STEPS.find((s) => s.docNumber === docNumber)!;
    const current = rows[docNumber];
    const next: StepRow = {
      doc_number: docNumber,
      status: (patch.status ?? current?.status ?? "not_started") as StepStatus,
      note: patch.note !== undefined ? patch.note : current?.note ?? null,
    };
    setRows((p) => ({ ...p, [docNumber]: next }));
    const { error } = await supabase.from("process_steps").upsert(
      {
        project_id: projectId,
        doc_number: docNumber,
        title: def.title,
        phase: def.phase,
        description: def.description,
        app_link: def.appLink ?? null,
        status: next.status,
        note: next.note,
        sort_order: PROCESS_STEPS.indexOf(def),
      },
      { onConflict: "project_id,doc_number" }
    );
    if (error) toast.error("Kunde inte spara steget");
  };

  const statusFor = (doc: string): StepStatus => (rows[doc]?.status ?? "not_started") as StepStatus;

  const inactiveSteps = PROCESS_STEPS.filter((s) => statusFor(s.docNumber) === "not_applicable");

  const upsertExternal = async (docNumber: string, patch: Partial<ExternalRow>) => {
    const current = external[docNumber];
    const next: ExternalRow = {
      doc_number: docNumber,
      completed: patch.completed ?? current?.completed ?? false,
      completed_at: patch.completed_at !== undefined ? patch.completed_at : current?.completed_at ?? null,
      responsible: patch.responsible !== undefined ? patch.responsible : current?.responsible ?? null,
      url: patch.url !== undefined ? patch.url : current?.url ?? null,
      comment: patch.comment !== undefined ? patch.comment : current?.comment ?? null,
    };
    setExternal((p) => ({ ...p, [docNumber]: next }));
    const { error } = await supabase.from("external_step_completions").upsert(
      { project_id: projectId, ...next },
      { onConflict: "project_id,doc_number" }
    );
    if (error) return toast.error("Kunde inte spara kvitteringen");
    await upsert(docNumber, { status: next.completed ? "done" : "not_started" });
  };


  const phaseProgress = (phase: ProcessPhase) => {
    const steps = PROCESS_STEPS.filter((s) => s.phase === phase);
    const relevant = steps.filter((s) => statusFor(s.docNumber) !== "not_applicable");
    if (relevant.length === 0) return 100;
    const done = relevant.filter((s) => statusFor(s.docNumber) === "done").length;
    return Math.round((done / relevant.length) * 100);
  };

  if (loading) {
    return <div className="py-12 text-center text-muted-foreground">Laddar processen...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
          <div>
            <p className="text-sm font-medium">Så hänger dokumenten ihop</p>
            <p className="text-sm text-muted-foreground">
              Varje dokument bygger på tidigare underlag och lämnar vidare till nästa steg. Öppna guiden för
              hela kedjan, eller klicka på ett steg för att se varför just det behövs.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open(`/processguide/${projectId}`, "_self")}>
              Öppna processguiden
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(`/projektvarde/${projectId}`, "_self")}>
              Värde &amp; tillsynspaket
            </Button>
          </div>

        </CardContent>
      </Card>
      <DetailLevelPanel projectId={projectId} onChanged={loadSteps} />
      {inactiveSteps.length > 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
            <p className="text-sm text-muted-foreground">
              {inactiveSteps.length} dokument är markerade som ej aktuella i det här projektet.
            </p>
            <Button variant="outline" size="sm" onClick={() => setShowInactive((v) => !v)}>
              {showInactive ? "Dölj ej aktuella" : `Visa även ej aktuella (${inactiveSteps.length})`}
            </Button>
          </CardContent>
        </Card>
      )}
      {PROCESS_PHASES.map((phase) => {
        const steps = PROCESS_STEPS.filter(
          (s) =>
            s.phase === phase.key &&
            (showInactive || statusFor(s.docNumber) !== "not_applicable")
        );
        const progress = phaseProgress(phase.key);
        return (
          <Card key={phase.key} className="overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base">{phase.label}</CardTitle>
                  <p className="text-sm text-muted-foreground">{phase.description}</p>
                </div>
                <div className="w-48">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Klart</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ul className="divide-y">
                {steps.map((step) => {
                  const status = statusFor(step.docNumber);
                  const isOpen = expanded === step.docNumber;
                  return (
                    <li key={step.docNumber} className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">{statusIcon(status)}</div>
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => setExpanded(isOpen ? null : step.docNumber)}
                            className="text-left w-full"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-xs text-muted-foreground">{step.docNumber}</span>
                              <span className="font-medium">{step.title}</span>
                              <Badge variant={statusVariant(status) as any} className="text-[10px]">
                                {STEP_STATUS_LABELS[status]}
                              </Badge>
                              <Badge variant="outline" className="text-[10px]">{step.owner}</Badge>
                              {step.externallyManaged && (
                                <Badge variant="secondary" className="text-[10px]">Hanteras externt</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-0.5">{step.description}</p>
                            {step.docNumber === "12" && ampSummary && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {ampSummary.done} av {ampSummary.total} avsnitt klara
                                {ampSummary.missing.length > 0
                                  ? ` · underlag saknas: ${ampSummary.missing.join(", ")}`
                                  : " · allt underlag hämtat"}
                              </p>
                            )}
                          </button>

                          {isOpen && (
                            <div className="mt-3 space-y-3">
                              <DocumentExplanation docNumber={step.docNumber} variant="panel" />
                              {step.afs && step.afs.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {step.afs.map((a) => (
                                    <Badge key={a} variant="secondary" className="text-[10px]">{a}</Badge>
                                  ))}
                                </div>
                              )}
                              {step.externallyManaged && (
                                <div className="rounded-lg border bg-muted/40 p-3 space-y-3">
                                  <label className="flex items-center gap-2 text-sm font-medium">
                                    <Checkbox
                                      checked={external[step.docNumber]?.completed ?? false}
                                      onCheckedChange={(v) =>
                                        upsertExternal(step.docNumber, {
                                          completed: v === true,
                                          completed_at:
                                            v === true
                                              ? external[step.docNumber]?.completed_at ??
                                                new Date().toISOString().slice(0, 10)
                                              : null,
                                        })
                                      }
                                    />
                                    Utfört externt och kvitterat
                                  </label>
                                  <div className="grid gap-2 sm:grid-cols-3">
                                    <Input
                                      type="date"
                                      value={external[step.docNumber]?.completed_at ?? ""}
                                      onChange={(e) =>
                                        upsertExternal(step.docNumber, { completed_at: e.target.value || null })
                                      }
                                    />
                                    <Input
                                      placeholder="Ansvarig / utförare"
                                      defaultValue={external[step.docNumber]?.responsible ?? ""}
                                      onBlur={(e) => upsertExternal(step.docNumber, { responsible: e.target.value })}
                                    />
                                    <Input
                                      placeholder="Länk eller diarienummer"
                                      defaultValue={external[step.docNumber]?.url ?? ""}
                                      onBlur={(e) => upsertExternal(step.docNumber, { url: e.target.value })}
                                    />
                                  </div>
                                  <Textarea
                                    rows={2}
                                    placeholder="Kommentar om hur steget hanterats externt"
                                    defaultValue={external[step.docNumber]?.comment ?? ""}
                                    onBlur={(e) => upsertExternal(step.docNumber, { comment: e.target.value })}
                                  />
                                </div>
                              )}
                              <div className="flex flex-wrap gap-2">
                                {(Object.keys(STEP_STATUS_LABELS) as StepStatus[]).map((s) => (
                                  <Button
                                    key={s}
                                    size="sm"
                                    variant={status === s ? "default" : "outline"}
                                    onClick={() => upsert(step.docNumber, { status: s })}
                                  >
                                    {STEP_STATUS_LABELS[s]}
                                  </Button>
                                ))}
                                {step.appLink && onOpenLink && (
                                  <Button size="sm" variant="ghost" onClick={() => onOpenLink(step.appLink!)}>
                                    Öppna <ChevronRight className="h-3 w-3 ml-1" />
                                  </Button>
                                )}
                              </div>
                              <Textarea
                                placeholder="Anteckning / hänvisning till handling"
                                defaultValue={rows[step.docNumber]?.note ?? ""}
                                onBlur={(e) => upsert(step.docNumber, { note: e.target.value })}
                                rows={2}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default ProcessRoadmap;
